import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  INotificationBaseRelatedToShow,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { RequestKey } from "api/request-key";

const KEYS = [
  NotificationType.SUBMITTED_TASK_INSTANCE,
  NotificationType.DRAFTED_TASK_INSTANCE,
  NotificationType.APPROVED_TASK_INSTANCE,
  NotificationType.REJECTED_TASK_INSTANCE,
  NotificationType.REVISION_REQUESTED_TASK_INSTANCE,
  NotificationType.REVIEWER_DECISION_TASK_INSTANCE,
  NotificationType.UPDATED_TASK_REVIEWERS,
  NotificationType.UPDATED_TASK,
  NotificationType.CREATED_TASK,
  NotificationType.DELETED_TASK,
];

/** Count-only query driving the sidebar review badge. */
export default function useTaskReviewCount(
  showId?: string
): {
  count: number;
  isLoading: boolean;
} {
  const queryClient = useQueryClient();
  const queryKey = [RequestKey.GetTaskReviewInboxCountApiV2, showId];

  const { data, isFetching } = useQuery({
    queryKey,
    queryFn: async () =>
      showId ? apiV2.task.getTaskReviewInboxCount({ showId }) : { count: 0 },
    enabled: !!showId,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!showId) return;
    const listenerId = `USE_TASK_REVIEW_COUNT:${showId}`;
    const invalidate = async (payload: INotificationBaseRelatedToShow) => {
      if (payload.showId !== showId) return;
      await queryClient.invalidateQueries({
        queryKey: [RequestKey.GetTaskReviewInboxCountApiV2, showId],
      });
    };
    for (const key of KEYS) {
      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key,
        showId,
        listenerId,
        callback: invalidate,
      });
    }
    return () => {
      for (const key of KEYS) {
        apiV2.notification.unsubscribe({ key, showId, listenerId });
      }
    };
  }, [showId, queryClient]);

  return { count: data?.count ?? 0, isLoading: isFetching };
}
